import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../../services/supabaseClient';
import Header from '../../../components/Header';
import { Helmet } from 'react-helmet';
import { 
  Building, MapPin, Calendar, ArrowLeft, Shield, 
  CheckCircle, ArrowRight, BedDouble, Bath, Home,
  ShieldCheck, Star
} from 'lucide-react';

export default function HabitatLandlordProfile() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [landlord, setLandlord] = useState<any>(null);
  const [listings, setListings] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) fetchLandlordData(id);
  }, [id]);

  const fetchLandlordData = async (landlordId: string) => {
    try {
      // 1. Récupérer les infos du profil propriétaire
      const { data: profileData, error: profileError } = await supabase
        .from('habitat_profiles')
        .select('*')
        .eq('id', landlordId)
        .maybeSingle();
      
      if (profileError) throw profileError;
      if (profileData) setLandlord(profileData);

      // 2. Récupérer TOUTES ses annonces publiées
      const { data: listingsData, error: listingsError } = await supabase
        .from('habitat_listings')
        .select('*')
        .eq('landlord_id', landlordId)
        .eq('status', 'published')
        .order('created_at', { ascending: false });
        
      if (listingsError) throw listingsError;
      if (listingsData) setListings(listingsData);
    } catch (err) {
      console.error("Erreur lors de la récupération du profil :", err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }
  
  if (!landlord) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4">
        <Building className="w-16 h-16 text-slate-300 mb-4" />
        <h2 className="text-2xl font-black text-slate-800">Profil introuvable</h2>
        <p className="text-slate-500 mb-6 font-bold">Ce propriétaire n'existe pas ou son compte a été désactivé.</p>
        <button onClick={() => navigate('/habitat')} className="bg-slate-900 text-white px-6 py-3 rounded-xl font-bold hover:bg-teal-600 transition-colors">
          Retour aux annonces
        </button>
      </div>
    );
  }

  const landlordName = landlord.full_name || 'Propriétaire';
  const initial = landlordName.charAt(0).toUpperCase();

  return (
    <div className="min-h-screen bg-slate-50 font-sans pt-20 pb-20 selection:bg-teal-500 selection:text-white">
      <Helmet>
        <title>{landlordName} - Propriétaire | Forfeo Habitat</title>
      </Helmet>

      <Header />
      
      <main className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex items-center gap-4 mb-8">
          <button onClick={() => window.history.back()} className="flex items-center gap-2 text-slate-500 hover:text-teal-600 font-bold transition">
            <ArrowLeft size={16} /> Retour
          </button>
          <div className="w-px h-4 bg-slate-300"></div>
          <button onClick={() => navigate('/habitat')} className="flex items-center gap-2 text-slate-500 hover:text-teal-600 font-bold transition">
            <Home size={16} /> Toutes les annonces
          </button>
        </div>

        {/* --- HEADER DU PROFIL (STYLE MINI-SITE) --- */}
        <div className="bg-slate-900 rounded-[3rem] p-8 md:p-16 text-white shadow-2xl relative overflow-hidden border border-slate-800 mb-12">
          {/* Effet lumineux de fond */}
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-teal-500/10 rounded-full blur-[100px] pointer-events-none -translate-y-1/2 translate-x-1/4"></div>

          <div className="relative z-10 flex flex-col md:flex-row items-center md:items-start gap-8 text-center md:text-left">
            <div className="w-32 h-32 md:w-40 md:h-40 bg-gradient-to-br from-teal-400 to-teal-600 rounded-full flex items-center justify-center text-5xl font-black text-white shadow-lg border-4 border-slate-800 shrink-0">
              {initial}
            </div>
            
            <div className="flex-1">
              <div className="flex flex-col md:flex-row items-center justify-center md:justify-start gap-4 mb-4">
                <h1 className="text-4xl md:text-5xl font-black tracking-tight">{landlordName}</h1>
                <div className="bg-teal-500/20 text-teal-400 px-4 py-1.5 rounded-full text-xs font-black uppercase tracking-widest flex items-center gap-1.5 border border-teal-500/30">
                  <ShieldCheck size={16} /> Partenaire vérifié
                </div>
              </div>
              
              <p className="text-slate-400 text-lg max-w-2xl leading-relaxed mb-8 mx-auto md:mx-0">
                Professionnel de l'immobilier partenaire de Forfeo Explore Québec. Gestionnaire de logements certifiés respectant les standards de qualité de la plateforme.
              </p>

              <div className="flex flex-wrap items-center justify-center md:justify-start gap-4">
                <div className="flex items-center gap-2 bg-white/5 border border-white/10 px-5 py-3 rounded-2xl">
                  <Building className="text-teal-400" size={20} />
                  <span className="font-bold text-slate-200">{listings.length} {listings.length > 1 ? 'Logements gérés' : 'Logement géré'}</span>
                </div>
                <div className="flex items-center gap-2 bg-white/5 border border-white/10 px-5 py-3 rounded-2xl">
                  <Star className="text-amber-400" size={20} />
                  <span className="font-bold text-slate-200">Évaluation : Excellente</span>
                </div>
                <div className="flex items-center gap-2 bg-white/5 border border-white/10 px-5 py-3 rounded-2xl">
                  <Calendar className="text-blue-400" size={20} />
                  <span className="font-bold text-slate-200">Membre depuis {new Date().getFullYear()}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* --- LISTE DES ANNONCES DU PROPRIO --- */}
        <div className="mb-8 flex items-center gap-4">
          <h2 className="text-3xl font-black text-slate-900 tracking-tight">Logements disponibles</h2>
          <div className="h-px flex-1 bg-slate-200"></div>
        </div>

        {listings.length === 0 ? (
          <div className="bg-white border-2 border-dashed border-slate-300 rounded-[3rem] p-16 text-center">
            <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-6">
              <Home className="w-10 h-10 text-slate-400" />
            </div>
            <h3 className="text-xl font-black text-slate-800 mb-2">Aucun logement en ce moment</h3>
            <p className="text-slate-500 font-medium">Ce propriétaire n'a pas d'annonce active pour l'instant.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {listings.map(listing => (
              <div 
                key={listing.id} 
                onClick={() => navigate(`/habitat/logement/${listing.id}`)}
                className="bg-white rounded-[2.5rem] border border-slate-200 overflow-hidden shadow-sm hover:shadow-xl hover:border-teal-300 transition-all duration-500 cursor-pointer group flex flex-col"
              >
                <div className="h-56 relative overflow-hidden bg-slate-100">
                  <img src={listing.images?.[0] || 'https://images.unsplash.com/photo-1502672260266-1c1de2d9d000'} alt={listing.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000" />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  
                  <div className="absolute top-4 left-4 bg-white/95 backdrop-blur-md px-3 py-1.5 rounded-full text-xs font-black text-teal-700 flex items-center gap-1 shadow-sm">
                    <CheckCircle size={14} /> Vérifié
                  </div>

                  <div className="absolute bottom-4 left-4 bg-slate-900 text-white px-4 py-2 rounded-xl text-sm font-black shadow-xl border border-white/20 translate-y-2 group-hover:translate-y-0 transition-transform duration-500">
                    {listing.monthly_rent} $ / mois
                  </div>
                </div>
                
                <div className="p-6 flex-1 flex flex-col">
                  <div className="flex items-center gap-1.5 text-xs font-black text-teal-600 uppercase tracking-widest bg-teal-50 px-3 py-1 rounded-lg w-fit mb-3">
                    <MapPin size={12} /> {listing.city}
                  </div>
                  
                  <h3 className="text-xl font-black text-slate-900 leading-tight mb-4 group-hover:text-teal-600 transition-colors line-clamp-2">
                    {listing.title}
                  </h3>
                  
                  <div className="flex items-center gap-3 text-xs font-bold text-slate-500 mb-6 mt-auto">
                    <span className="flex items-center gap-1.5 bg-slate-50 px-2 py-1 rounded-lg border border-slate-100"><BedDouble size={14}/> {listing.bedrooms} ch.</span>
                    <span className="flex items-center gap-1.5 bg-slate-50 px-2 py-1 rounded-lg border border-slate-100"><Bath size={14}/> {listing.bathrooms} sdb</span>
                  </div>

                  <div className="pt-4 border-t border-slate-100 flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2 text-slate-500 font-bold">
                      <Calendar size={14} className="text-slate-400" />
                      {listing.available_from ? new Date(listing.available_from).toLocaleDateString('fr-CA') : 'Maintenant'}
                    </span>
                    <span className="text-teal-600 group-hover:translate-x-1 transition-transform">
                      <ArrowRight size={18} />
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
